// src/pages/ConnectionManagerModal.tsx
// Updated to use SchemaExplorer for the browse/select flow.

import React, { useState, useEffect } from "react";
import { SchemaExplorer, SchemaSelection } from "./SchemaExplorer";
import { apiGet, apiPost, apiPut, apiDelete } from "../API";

// ─── Types ────────────────────────────────────────────────────────────────────

export interface ConnectorType {
  type: string;
  display_name: string;
  supports_write: boolean;
  supports_schema_browse: boolean;
}

export interface SavedConnection {
  id: string;
  name: string;
  connector_type: string;
  env_tag?: string;
  host?: string;
  port?: number;
  database_name?: string;
  username?: string;
  extra_config?: Record<string, any>;
  is_active: boolean;
  last_tested_at?: string;
  last_test_ok?: boolean;
  created_at: string;
}

export interface SelectedSource {
  connection_id: string;
  connection_name: string;
  connector_type: string;
  schema: string;
  /** Maps bare table name → its schema. Populated when tables span multiple schemas. */
  tableSchemas?: Record<string, string>;
  tables: string[];
  columns: Record<string, string[]>;
  relationships: Array<{ from_schema: string; from_table: string; from_column: string; to_schema: string; to_table: string; to_column: string }>;
  custom_sql?: string;
}

// ─── Connector field definitions ──────────────────────────────────────────────

const CONNECTOR_FIELDS: Record<string, Array<{
  key: string; label: string; type: "text" | "password" | "number";
  placeholder?: string; required?: boolean;
  credField?: boolean;
  extraField?: boolean;
}>> = {
  postgresql: [
    { key: "host",          label: "Host",     type: "text",     placeholder: "localhost",   required: true },
    { key: "port",          label: "Port",     type: "number",   placeholder: "5432",        required: true },
    { key: "database_name", label: "Database", type: "text",     placeholder: "my_database", required: true },
    { key: "username",      label: "Username", type: "text",     placeholder: "postgres",    required: true },
    { key: "password",      label: "Password", type: "password", placeholder: "••••••••",    credField: true },
  ],
  snowflake: [
    { key: "account",        label: "Account",        type: "text",     placeholder: "xy12345.us-east-1", required: true, extraField: true },
    { key: "username",       label: "Username",       type: "text",     placeholder: "my_user",    required: true },
    { key: "password",       label: "Password",       type: "password", placeholder: "••••••••",   credField: true },
    { key: "database_name",  label: "Database",       type: "text",     placeholder: "MY_DB" },
    { key: "warehouse",      label: "Warehouse",      type: "text",     placeholder: "COMPUTE_WH", extraField: true },
    { key: "role",           label: "Role",           type: "text",     placeholder: "SYSADMIN",   extraField: true },
    { key: "default_schema", label: "Default Schema", type: "text",     placeholder: "PUBLIC",     extraField: true },
  ],
  aws_glue: [
    { key: "aws_region",            label: "AWS Region",        type: "text",     placeholder: "us-east-1",        required: true, extraField: true },
    { key: "aws_access_key_id",     label: "Access Key ID",     type: "text",     placeholder: "AKIA…",            extraField: true },
    { key: "aws_secret_access_key", label: "Secret Access Key", type: "password", placeholder: "••••••••",         credField: true },
    { key: "athena_output_s3",      label: "Athena Output S3",  type: "text",     placeholder: "s3://bucket/pfx/", extraField: true },
    { key: "athena_workgroup",      label: "Athena Workgroup",  type: "text",     placeholder: "primary",          extraField: true },
  ],
  mysql: [
    { key: "host",          label: "Host",     type: "text",     placeholder: "localhost", required: true },
    { key: "port",          label: "Port",     type: "number",   placeholder: "3306",      required: true },
    { key: "database_name", label: "Database", type: "text",     placeholder: "my_db",     required: true },
    { key: "username",      label: "Username", type: "text",     placeholder: "root",      required: true },
    { key: "password",      label: "Password", type: "password", placeholder: "••••••••",  credField: true },
  ],
};

const CONNECTOR_ICONS: Record<string, string> = {
  postgresql: "🐘", snowflake: "❄️", aws_glue: "☁️",
  mysql: "🐬", redshift: "🔴", bigquery: "📊", databricks: "🧱",
};

const ENV_COLORS: Record<string, { bg: string; text: string; border: string }> = {
  prod:    { bg: "#fef2f2", text: "#991b1b", border: "#fecaca" },
  staging: { bg: "#fffbeb", text: "#92400e", border: "#fde68a" },
  dev:     { bg: "#f0fdf4", text: "#166534", border: "#bbf7d0" },
};

// ─── CSS ─────────────────────────────────────────────────────────────────────

const CM_CSS = `
  @import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Mono:wght@400;500&family=Sora:wght@400;500;600;700&display=swap');

  .cm * { box-sizing:border-box; margin:0; padding:0; }
  .cm { font-family:'Sora',sans-serif; color:#0f172a; }

  .cm-overlay {
    position:fixed; inset:0; background:rgba(15,23,42,0.6);
    display:flex; align-items:center; justify-content:center;
    z-index:1000; backdrop-filter:blur(4px);
    animation:cm-fadein 0.18s ease;
  }
  @keyframes cm-fadein  { from{opacity:0} to{opacity:1} }
  @keyframes cm-slidein { from{opacity:0;transform:translateY(20px)} to{opacity:1;transform:translateY(0)} }

  /* Wider panel when in explorer mode */
  .cm-panel {
    background:#fff; border-radius:16px;
    box-shadow:0 24px 64px rgba(15,23,42,0.18), 0 0 0 1px rgba(15,23,42,0.06);
    width:min(96vw,940px); max-height:90vh;
    display:flex; flex-direction:column;
    animation:cm-slidein 0.22s ease;
    transition:width 0.25s ease;
  }
  .cm-panel.explorer-mode {
    width:min(98vw,1280px);
    max-height:92vh;
  }

  .cm-header {
    padding:20px 24px 0; border-bottom:1px solid #f1f5f9;
    display:flex; align-items:flex-start; justify-content:space-between;
  }
  .cm-title { font-size:17px; font-weight:700; color:#0f172a; letter-spacing:-0.3px; }
  .cm-sub   { font-size:12.5px; color:#64748b; margin-top:3px; }

  .cm-close {
    all:unset; box-sizing:border-box; cursor:pointer;
    width:32px; height:32px; border-radius:8px;
    display:flex; align-items:center; justify-content:center;
    color:#94a3b8; transition:all 0.12s; border:1px solid transparent;
  }
  .cm-close:hover { background:#f1f5f9; color:#475569; border-color:#e2e8f0; }

  .cm-tabs { display:flex; gap:4px; padding:12px 24px 0; border-bottom:1px solid #e2e8f0; }
  .cm-tab {
    all:unset; box-sizing:border-box; cursor:pointer;
    padding:8px 14px; font-size:12.5px; font-weight:500; color:#64748b;
    border-radius:8px 8px 0 0; transition:all 0.12s;
    border:1px solid transparent; border-bottom:none; position:relative; bottom:-1px;
  }
  .cm-tab:hover { color:#0f172a; background:#f8fafc; }
  .cm-tab.active { color:#4f46e5; background:#fff; border-color:#e2e8f0; border-bottom-color:#fff; }

  .cm-body { flex:1; overflow-y:auto; padding:20px 24px; min-height:0; }
  .cm-body.no-pad { padding:0; overflow:hidden; display:flex; flex-direction:column; }
  .cm-body::-webkit-scrollbar { width:4px; }
  .cm-body::-webkit-scrollbar-thumb { background:#e2e8f0; border-radius:99px; }

  .cm-conn-grid { display:grid; grid-template-columns:repeat(3,1fr); gap:10px; }

  .cm-conn-type {
    border:2px solid #e2e8f0; border-radius:12px; padding:13px;
    cursor:pointer; transition:all 0.15s; background:#fafafa;
    display:flex; align-items:center; gap:10px;
  }
  .cm-conn-type:hover    { border-color:#a5b4fc; background:#f5f3ff; }
  .cm-conn-type.selected { border-color:#4f46e5; background:#eef2ff; }
  .cm-conn-icon  { font-size:21px; flex-shrink:0; }
  .cm-conn-name  { font-size:12.5px; font-weight:600; color:#1e293b; }
  .cm-conn-badge { font-size:10px; color:#64748b; }

  .cm-form { display:flex; flex-direction:column; gap:13px; }
  .cm-row2 { display:grid; grid-template-columns:1fr 1fr; gap:13px; }
  .cm-label { display:block; font-size:11px; font-weight:600; color:#374151; margin-bottom:4px; letter-spacing:0.2px; }
  .cm-input {
    width:100%; padding:8px 11px;
    border:1.5px solid #e2e8f0; border-radius:8px;
    font-family:'Sora',sans-serif; font-size:13px; color:#0f172a;
    background:#fafafa; outline:none; transition:all 0.15s;
  }
  .cm-input:focus { border-color:#6366f1; background:#fff; box-shadow:0 0 0 3px rgba(99,102,241,0.08); }
  .cm-input::placeholder { color:#94a3b8; }

  .cm-section-label {
    font-family:'IBM Plex Mono',monospace;
    font-size:9px; font-weight:600; letter-spacing:1.5px;
    text-transform:uppercase; color:#94a3b8; margin-bottom:9px;
  }

  .cm-test-bar {
    padding:11px 14px; border-radius:10px;
    display:flex; align-items:center; gap:10px; font-size:12.5px; min-height:46px;
  }
  .cm-test-bar.idle    { background:#f8fafc; border:1px solid #e2e8f0; color:#475569; }
  .cm-test-bar.testing { background:#eff6ff; border:1px solid #bfdbfe; color:#1d4ed8; }
  .cm-test-bar.ok      { background:#f0fdf4; border:1px solid #86efac; color:#166534; }
  .cm-test-bar.fail    { background:#fef2f2; border:1px solid #fca5a5; color:#991b1b; }
  .cm-test-detail { font-family:'IBM Plex Mono',monospace; font-size:10.5px; margin-top:3px; opacity:0.8; word-break:break-word; }
  .cm-latency { font-family:'IBM Plex Mono',monospace; font-size:10.5px; padding:2px 7px; border-radius:4px; background:rgba(0,0,0,0.06); }

  .cm-btn {
    all:unset; box-sizing:border-box; cursor:pointer;
    display:inline-flex; align-items:center; justify-content:center; gap:6px;
    padding:9px 18px; border-radius:9px; font-family:'Sora',sans-serif;
    font-size:13px; font-weight:600; transition:all 0.13s;
  }
  .cm-btn-primary { background:#4f46e5; color:#fff; }
  .cm-btn-primary:hover { background:#4338ca; }
  .cm-btn-primary:disabled { background:#c7d2fe; color:#fff; cursor:not-allowed; }
  .cm-btn-ghost { border:1.5px solid #e2e8f0; color:#475569; background:#fff; }
  .cm-btn-ghost:hover { border-color:#c7d2fe; color:#4f46e5; background:#f5f3ff; }
  .cm-btn-ghost:disabled { opacity:0.5; cursor:not-allowed; }

  .cm-footer {
    padding:14px 24px; border-top:1px solid #f1f5f9;
    display:flex; align-items:center; justify-content:flex-end; gap:10px; flex-shrink:0;
  }

  /* Saved connections list */
  .cm-conn-row {
    display:flex; align-items:center; gap:12px;
    padding:11px 13px; border:1.5px solid #e2e8f0; border-radius:10px;
    background:#fafafa; cursor:pointer; transition:all 0.15s; margin-bottom:8px;
  }
  .cm-conn-row:hover { border-color:#a5b4fc; background:#f5f3ff; }

  /* Schema picker */
  .cm-schema-grid { display:grid; grid-template-columns:repeat(auto-fill, minmax(150px,1fr)); gap:8px; margin-top:12px; }
  .cm-schema-card {
    padding:10px 12px; border:1.5px solid #e2e8f0; border-radius:10px;
    background:#fff; cursor:pointer; transition:all 0.15s;
    display:flex; align-items:center; gap:9px; font-size:13px; font-weight:600; color:#1e293b;
  }
  .cm-schema-card:hover { border-color:#818cf8; background:#f5f3ff; color:#4f46e5; }
  .cm-schema-card.selected { border-color:#4f46e5; background:#eef2ff; color:#4f46e5; }

  .cm-spinner {
    width:14px; height:14px; border-radius:50%;
    border:2px solid currentColor; border-top-color:transparent;
    animation:cm-spin 0.7s linear infinite; flex-shrink:0;
  }
  @keyframes cm-spin { to { transform:rotate(360deg); } }

  .cm-empty { color:#94a3b8; font-size:13px; text-align:center; padding:24px; }
`;

// ─── Helpers ─────────────────────────────────────────────────────────────────

//const API = (process.env.REACT_APP_API_BASE || "http://localhost:3003") + "/adriva_api/connections";

const CM_API_BASE = "/adriva_api/connections";

// Wrappers around the central api.ts helpers so the rest of the modal can keep
// its existing `apiFetch(path, opts)` signature with minimal churn.
async function apiFetch(path: string, opts?: { method?: string; body?: string }) {
  const method = (opts?.method || "GET").toUpperCase();
  const url    = CM_API_BASE + path;
  const body   = opts?.body ? JSON.parse(opts.body) : undefined;
  switch (method) {
    case "GET":    return apiGet(url);
    case "POST":   return apiPost(url, body);
    case "PUT":    return apiPut(url, body);
    case "DELETE": return apiDelete(url);
    default:       throw new Error(`Unsupported method ${method}`);
  }
}

function buildTestPreviewPayload(connectorType: string, formValues: Record<string, string>) {
  const fields = CONNECTOR_FIELDS[connectorType] || [];
  const DIRECT_CRED_KEYS = new Set(["password", "private_key", "aws_access_key_id", "aws_secret_access_key", "service_account_json"]);
  const DIRECT_BASE_KEYS = new Set(["host", "port", "database_name", "username"]);
  const payload: Record<string, any> = { connector_type: connectorType };
  const extra_config: Record<string, string> = {};

  for (const f of fields) {
    const val = formValues[f.key];
    if (!val) continue;
    if (DIRECT_BASE_KEYS.has(f.key))  payload[f.key] = f.type === "number" ? parseInt(val, 10) : val;
    else if (DIRECT_CRED_KEYS.has(f.key)) payload[f.key] = val;
    else extra_config[f.key] = val;
  }
  if (Object.keys(extra_config).length) payload.extra_config = extra_config;
  return payload;
}

const Spinner = () => <div className="cm-spinner" />;

function EnvTag({ env }: { env?: string }) {
  if (!env) return null;
  const c = ENV_COLORS[env] || ENV_COLORS.dev;
  return (
    <span style={{ fontSize: 10, fontWeight: 600, padding: "1px 7px", borderRadius: 99, background: c.bg, color: c.text, border: `1px solid ${c.border}` }}>
      {env.toUpperCase()}
    </span>
  );
}

// ─── Browse state machine ──────────────────────────────────────────────────
// idle → conn_list → schema_pick → explorer → (done)

type BrowseStep = "conn_list" | "schema_pick" | "explorer";

// ─── Main Modal ───────────────────────────────────────────────────────────────

interface Props {
  onClose: () => void;
  onSelect: (src: SelectedSource) => void;
}

export const ConnectionManagerModal: React.FC<Props> = ({ onClose, onSelect }) => {
  const [tab, setTab] = useState<"list" | "create">("list");

  // Connection data
  const [connTypes, setConnTypes]     = useState<ConnectorType[]>([]);
  const [savedConns, setSavedConns]   = useState<SavedConnection[]>([]);
  const [loadingConns, setLoadingConns] = useState(true);

  // Browse flow
  const [browseStep, setBrowseStep]     = useState<BrowseStep>("conn_list");
  const [browseConn, setBrowseConn]     = useState<SavedConnection | null>(null);
  const [schemas, setSchemas]           = useState<string[]>([]);
  const [loadingSchemas, setLoadingSchemas] = useState(false);
  const [selSchemas, setSelSchemas]     = useState<string[]>([]);

  // Create form
  const [selType, setSelType]         = useState<string>("");
  const [formValues, setFormValues]   = useState<Record<string, string>>({});
  const [connName, setConnName]       = useState("");
  const [envTag, setEnvTag]           = useState("dev");
  const [creating, setCreating]       = useState(false);
  const [saveEnabled, setSaveEnabled] = useState(false);

  // Test
  const [testState, setTestState]   = useState<"idle" | "testing" | "ok" | "fail">("idle");
  const [testMsg, setTestMsg]       = useState("");
  const [testDetail, setTestDetail] = useState("");
  const [latencyMs, setLatencyMs]   = useState<number | null>(null);

  const isExplorerMode = tab === "list" && browseStep === "explorer";

  useEffect(() => {
    apiFetch("/types").then(setConnTypes).catch(console.error);
    loadSavedConns();
  }, []);

  const loadSavedConns = async () => {
    setLoadingConns(true);
    try { setSavedConns(await apiFetch("")); }
    catch (e) { console.error(e); }
    finally { setLoadingConns(false); }
  };

  // ── Browse flow ───────────────────────────────────────────────────────────

  const openConn = async (conn: SavedConnection) => {
    setBrowseConn(conn);
    setLoadingSchemas(true);
    setBrowseStep("schema_pick");
    setSelSchemas([]);
    try {
      const data = await apiFetch(`/${conn.id}/schemas`);
      setSchemas(data);
      // If only one schema, auto-select and skip straight to explorer
      if (data.length === 1) {
        setSelSchemas([data[0]]);
        setBrowseStep("explorer");
      }
    } finally {
      setLoadingSchemas(false);
    }
  };

  const confirmSchemas = () => {
    if (selSchemas.length > 0) setBrowseStep("explorer");
  };

  const toggleSchema = (s: string) => {
    setSelSchemas(prev =>
      prev.includes(s) ? prev.filter(x => x !== s) : [...prev, s]
    );
  };

  const backToConnList = () => {
    setBrowseStep("conn_list");
    setBrowseConn(null);
    setSchemas([]);
    setSelSchemas([]);
  };

  const backToSchemas = () => {
    setBrowseStep("schema_pick");
    setSelSchemas([]);
  };

  const handleSchemaSelection = (selection: SchemaSelection) => {
    if (!browseConn) return;
    onSelect({
      connection_id:   browseConn.id,
      connection_name: browseConn.name,
      connector_type:  browseConn.connector_type,
      schema:          selSchemas[0] || "",
      tableSchemas:    selection.tableSchemas,
      tables:          selection.tables,
      columns:         selection.columns,
      relationships:   selection.relationships,
      custom_sql:      selection.custom_sql,
    });
    onClose();
  };

  // ── Create + Test ─────────────────────────────────────────────────────────

  const handleTestNew = async () => {
    if (!selType) return;
    setTestState("testing"); setTestMsg("Connecting…"); setTestDetail(""); setLatencyMs(null); setSaveEnabled(false);
    try {
      const result = await apiFetch("/test-preview", {
        method: "POST",
        body: JSON.stringify(buildTestPreviewPayload(selType, formValues)),
      });
      if (result.ok) {
        setTestState("ok"); setTestMsg("Connection successful"); setLatencyMs(result.latency_ms ?? null);
        setTestDetail(result.server_version || ""); setSaveEnabled(true);
      } else {
        setTestState("fail"); setTestMsg(result.message || "Connection failed"); setSaveEnabled(false);
      }
    } catch (e: any) {
      setTestState("fail"); setTestMsg("Could not reach the API"); setTestDetail(e.message || ""); setSaveEnabled(false);
    }
  };

  const handleCreate = async () => {
    if (!selType || !connName.trim()) return;
    setCreating(true);
    try {
      const fields = CONNECTOR_FIELDS[selType] || [];
      const credentials: Record<string, string> = {};
      const extra_config: Record<string, string> = {};
      const base: Record<string, any> = {};
      for (const f of fields) {
        const val = formValues[f.key] || "";
        if (!val) continue;
        if (f.credField) credentials[f.key] = val;
        else if (["host", "port", "database_name", "username"].includes(f.key)) base[f.key] = f.type === "number" ? parseInt(val, 10) : val;
        else extra_config[f.key] = val;
      }
      await apiFetch("", {
        method: "POST",
        body: JSON.stringify({ name: connName.trim(), connector_type: selType, env_tag: envTag, credentials, extra_config: Object.keys(extra_config).length ? extra_config : undefined, ...base }),
      });
      await loadSavedConns();
      setTab("list");
      resetForm();
    } catch (e: any) {
      alert("Failed to save: " + e.message);
    } finally { setCreating(false); }
  };

  const resetForm = () => {
    setSelType(""); setFormValues({}); setConnName(""); setEnvTag("dev");
    setTestState("idle"); setTestMsg(""); setTestDetail(""); setLatencyMs(null); setSaveEnabled(false);
  };

  const handleFormChange = (key: string, value: string) => {
    setFormValues(v => ({ ...v, [key]: value }));
    if (testState === "ok") { setTestState("idle"); setSaveEnabled(false); }
  };

  const handleTestSaved = async (conn: SavedConnection, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      const r = await apiFetch(`/${conn.id}/test`, { method: "POST" });
      alert(r.ok ? `✅ ${r.message} (${r.latency_ms}ms)${r.server_version ? `\n${r.server_version}` : ""}` : `❌ ${r.message}`);
    } catch (err: any) { alert("Test failed: " + err.message); }
  };

  const fields = CONNECTOR_FIELDS[selType] || [];
  const requiredFilled = fields.filter(f => f.required).every(f => !!formValues[f.key]?.trim());

  // ─────────────────────────────────────────────────────────────────────────────

  return (
    <div className="cm">
      <style>{CM_CSS}</style>
      <div className="cm-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
        <div className={`cm-panel${isExplorerMode ? " explorer-mode" : ""}`}>

          {/* Header */}
          <div className="cm-header" style={{ paddingBottom: 14 }}>
            <div>
              <div className="cm-title">
                {isExplorerMode && browseConn
                  ? `${CONNECTOR_ICONS[browseConn.connector_type] || "🔌"} ${browseConn.name} — ${selSchemas.join(", ")}`
                  : "Data Connections"
                }
              </div>
              <div className="cm-sub">
                {isExplorerMode
                  ? "Select tables, explore relationships, and define your data source"
                  : "Connect to databases, warehouses, and pipelines"
                }
              </div>
            </div>
            <button className="cm-close" onClick={onClose}>
              <svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </button>
          </div>

          {/* Tabs — hidden in explorer mode */}
          {!isExplorerMode && (
            <div className="cm-tabs">
              {[{ id: "list", label: "📋  Saved Connections" }, { id: "create", label: "＋  New Connection" }].map(t => (
                <button key={t.id} className={`cm-tab${tab === t.id ? " active" : ""}`}
                  onClick={() => { setTab(t.id as any); setBrowseStep("conn_list"); }}>
                  {t.label}
                </button>
              ))}
            </div>
          )}

          {/* Body */}
          <div className={`cm-body${isExplorerMode ? " no-pad" : ""}`}>

            {/* ── List tab ──────────────────────────────────────────── */}
            {tab === "list" && (

              /* ── State: connections list ── */
              browseStep === "conn_list" ? (
                <div>
                  <div className="cm-section-label">Select a connection to explore its data</div>
                  {loadingConns
                    ? <div style={{ display: "flex", gap: 8, color: "#64748b", fontSize: 13, padding: 8 }}><Spinner /> Loading…</div>
                    : savedConns.length === 0
                      ? <div className="cm-empty" style={{ marginTop: 20 }}>No connections yet. Create one using the tab above.</div>
                      : savedConns.map(conn => (
                        <div key={conn.id} className="cm-conn-row" onClick={() => openConn(conn)}>
                          <span style={{ fontSize: 22 }}>{CONNECTOR_ICONS[conn.connector_type] || "🔌"}</span>
                          <div style={{ flex: 1 }}>
                            <div style={{ fontWeight: 600, fontSize: 13.5, color: "#0f172a" }}>{conn.name}</div>
                            <div style={{ fontSize: 11.5, color: "#64748b", display: "flex", gap: 8, alignItems: "center", marginTop: 2 }}>
                              <span>{conn.connector_type}</span>
                              {conn.host && <span>· {conn.host}{conn.port ? `:${conn.port}` : ""}</span>}
                              {conn.database_name && <span>· {conn.database_name}</span>}
                            </div>
                          </div>
                          <EnvTag env={conn.env_tag} />
                          {conn.last_test_ok !== undefined && <span style={{ fontSize: 16 }}>{conn.last_test_ok ? "✅" : "❌"}</span>}
                          <button className="cm-btn cm-btn-ghost" style={{ padding: "5px 11px", fontSize: 11 }}
                            onClick={e => handleTestSaved(conn, e)}>Test</button>
                        </div>
                      ))
                  }
                </div>

              /* ── State: schema picker ── */
              ) : browseStep === "schema_pick" ? (
                <div>
                  <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
                    <button className="cm-btn cm-btn-ghost" style={{ padding: "5px 12px", fontSize: 12 }} onClick={backToConnList}>← Back</button>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: 14, color: "#0f172a" }}>
                        {CONNECTOR_ICONS[browseConn?.connector_type || ""] || "🔌"} {browseConn?.name}
                      </div>
                      <div style={{ fontSize: 12, color: "#64748b", marginTop: 2 }}>
                        Select one or more schemas to explore together
                      </div>
                    </div>
                    <EnvTag env={browseConn?.env_tag} />
                  </div>

                  {loadingSchemas
                    ? <div style={{ display: "flex", gap: 8, color: "#64748b", fontSize: 13, padding: 8 }}><Spinner /> Loading schemas…</div>
                    : schemas.length === 0
                      ? <div className="cm-empty">No schemas found in this connection.</div>
                      : (
                        <>
                          <div className="cm-schema-grid">
                            {schemas.map(s => {
                              const checked = selSchemas.includes(s);
                              return (
                                <div key={s}
                                  className={`cm-schema-card${checked ? " selected" : ""}`}
                                  onClick={() => toggleSchema(s)}
                                  style={{ cursor: "pointer" }}>
                                  {/* Checkbox */}
                                  <div style={{
                                    width: 16, height: 16, borderRadius: 4, flexShrink: 0,
                                    border: `1.5px solid ${checked ? "#4f46e5" : "#d1d5db"}`,
                                    background: checked ? "#4f46e5" : "#fff",
                                    display: "flex", alignItems: "center", justifyContent: "center",
                                    transition: "all 0.12s",
                                  }}>
                                    {checked && (
                                      <svg width="9" height="9" viewBox="0 0 10 10" fill="none">
                                        <path d="M1.5 5L4 7.5L8.5 2.5" stroke="#fff" strokeWidth="2" strokeLinecap="round"/>
                                      </svg>
                                    )}
                                  </div>
                                  <span style={{ fontSize: 16 }}>📁</span>
                                  <div style={{ flex: 1, minWidth: 0 }}>
                                    <div style={{ fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{s}</div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                          <div style={{ marginTop: 16, display: "flex", alignItems: "center", gap: 10 }}>
                            <button
                              className="cm-btn cm-btn-primary"
                              disabled={selSchemas.length === 0}
                              onClick={confirmSchemas}
                            >
                              Explore {selSchemas.length > 0 ? `${selSchemas.length} schema${selSchemas.length > 1 ? "s" : ""}` : "…"} →
                            </button>
                            {selSchemas.length > 0 && (
                              <span style={{ fontSize: 11, color: "#64748b" }}>
                                {selSchemas.join(", ")}
                              </span>
                            )}
                          </div>
                        </>
                      )
                  }
                </div>

              /* ── State: explorer ── */
              ) : browseStep === "explorer" && browseConn ? (
                <SchemaExplorer
                  connectionId={browseConn.id}
                  schema={selSchemas[0] || ""}
                  extraSchemas={selSchemas.slice(1)}
                  onSelectionConfirm={handleSchemaSelection}
                  onBack={schemas.length > 1 ? backToSchemas : backToConnList}
                />
              ) : null
            )}

            {/* ── Create tab ─────────────────────────────────────────── */}
            {tab === "create" && (
              <div className="cm-form">
                <div>
                  <div className="cm-section-label">1 · Choose connector type</div>
                  <div className="cm-conn-grid">
                    {(connTypes.length > 0
                      ? connTypes
                      : [
                          { type: "postgresql", display_name: "PostgreSQL",    supports_write: true },
                          { type: "snowflake",  display_name: "Snowflake",     supports_write: true },
                          { type: "aws_glue",   display_name: "AWS Glue / S3", supports_write: false },
                          { type: "mysql",      display_name: "MySQL",          supports_write: true },
                        ]
                    ).map(ct => (
                      <div key={ct.type}
                        className={`cm-conn-type${selType === ct.type ? " selected" : ""}`}
                        onClick={() => { setSelType(ct.type); setFormValues({}); setTestState("idle"); setSaveEnabled(false); }}>
                        <span className="cm-conn-icon">{CONNECTOR_ICONS[ct.type] || "🔌"}</span>
                        <div>
                          <div className="cm-conn-name">{ct.display_name}</div>
                          <div className="cm-conn-badge">{"supports_write" in ct ? (ct.supports_write ? "Read/Write" : "Read only") : ""}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {selType && (
                  <>
                    <div>
                      <div className="cm-section-label">2 · Connection details</div>
                      <div className="cm-row2">
                        <div>
                          <label className="cm-label">Connection Name *</label>
                          <input className="cm-input" value={connName}
                            onChange={e => { setConnName(e.target.value); if (testState === "ok") { setTestState("idle"); setSaveEnabled(false); } }}
                            placeholder="e.g. Production Snowflake" />
                        </div>
                        <div>
                          <label className="cm-label">Environment</label>
                          <select className="cm-input" value={envTag} onChange={e => setEnvTag(e.target.value)}>
                            <option value="dev">Development</option>
                            <option value="staging">Staging</option>
                            <option value="prod">Production</option>
                          </select>
                        </div>
                      </div>
                    </div>

                    <div>
                      <div className="cm-section-label">3 · Connection parameters</div>
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 13 }}>
                        {fields.map(f => (
                          <div key={f.key} style={{ gridColumn: f.key.length > 16 ? "1/-1" : undefined }}>
                            <label className="cm-label">
                              {f.label}{f.required ? " *" : ""}
                              {f.credField && <span style={{ marginLeft: 6, fontSize: 9, color: "#94a3b8" }}>🔒 encrypted on save</span>}
                            </label>
                            <input
                              type={f.type === "number" ? "number" : f.type}
                              className="cm-input" placeholder={f.placeholder}
                              value={formValues[f.key] || ""}
                              onChange={e => handleFormChange(f.key, e.target.value)}
                            />
                          </div>
                        ))}
                      </div>
                    </div>

                    <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                      <div className={`cm-test-bar ${testState}`}>
                        {testState === "testing" && <Spinner />}
                        <div style={{ flex: 1 }}>
                          {testState === "idle"    && <span>Test your connection before saving</span>}
                          {testState === "testing" && <span>Connecting to {selType}…</span>}
                          {testState === "ok"      && <><span>✅ {testMsg}</span>{testDetail && <div className="cm-test-detail">{testDetail}</div>}</>}
                          {testState === "fail"    && <><span>❌ {testMsg}</span>{testDetail && <div className="cm-test-detail">{testDetail}</div>}</>}
                        </div>
                        {latencyMs !== null && testState === "ok" && <span className="cm-latency">{latencyMs}ms</span>}
                        {testState !== "testing" && (
                          <button className="cm-btn cm-btn-ghost" style={{ padding: "5px 13px", fontSize: 12, flexShrink: 0 }}
                            disabled={!requiredFilled} onClick={handleTestNew}>
                            {testState === "idle" ? "Test Connection" : testState === "ok" ? "Re-test" : "Retry"}
                          </button>
                        )}
                      </div>
                      {testState === "ok"   && <div style={{ fontSize: 11, color: "#166534" }}>✓ Connection verified — you can now save it.</div>}
                      {testState === "idle" && !saveEnabled && <div style={{ fontSize: 11, color: "#94a3b8" }}>You must test the connection successfully before saving.</div>}
                    </div>
                  </>
                )}
              </div>
            )}
          </div>

          {/* Footer — hidden in explorer mode (explorer has its own confirm button) */}
          {!isExplorerMode && (
            <div className="cm-footer">
              <button className="cm-btn cm-btn-ghost" onClick={onClose}>Cancel</button>
              {tab === "create" && selType && (
                <button className="cm-btn cm-btn-primary"
                  disabled={creating || !connName.trim() || !saveEnabled}
                  onClick={handleCreate}>
                  {creating ? <><Spinner /> Saving…</> : "Save Connection"}
                </button>
              )}
            </div>
          )}

        </div>
      </div>
    </div>
  );
};

export default ConnectionManagerModal;